import React, { useState } from 'react';
import {
  Container,
  CircularProgress,
  Grid2,
  Typography,
  Card,
  CardContent,
  Box,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  Divider,
  IconButton,
} from '@mui/material';
import ParkingDisplay from './ParkingTypes';
import PhotoCarousel from './PhotoCarousel';
import { 
  Close as CloseIcon,
  MeetingRoom as MeetingRoomIcon,
  SquareFoot as SquareFootIcon,
  Layers as FloorIcon,
  Bed as BedIcon,
  Bathtub as BathIcon,
  CalendarToday as CalendarIcon,
  Wifi as WifiIcon,
  Elevator as ElevatorIcon,
  PhoneAndroid as PhoneAndroidIcon,
  CameraOutdoor as CameraOutdoorIcon,
  School as SchoolIcon,
  Public as PublicIcon,
  Pets as PetsIcon,
  ChildFriendly as ChildFriendlyIcon,
  Man as ManIcon,
  Wc as CoupleIcon,
  Payment as PaymentIcon,
  EventAvailable,
  Balcony,
} from '@mui/icons-material';

const ApartmentListing = ({ apartments = [], loading, error }) => {
  const [selectedApartment, setSelectedApartment] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleCardClick = (apartment) => {
    setSelectedApartment(apartment);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedApartment(null);
  };

  const DetailItem = ({ icon: Icon, label, value }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
      <Icon color="secondary" sx={{ mr: 1 }} />
      <Typography variant="body2" color="textSecondary">
        {label && `${label}: `}
        {value}
      </Typography>
    </Box>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Container>
      <Grid2 container spacing={4}>
        {apartments.length > 0 ? (
          apartments.map((apartment, index) => (
            <Grid2 item xs={12} sm={6} md={4} key={index}>
              <Card
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%', // Full width of grid item
                  height: 350, // Fixed total height for all cards
                  maxWidth: 300,
                  minWidth: 300,
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  '&:hover': {
                    transform: { xs: 'none', sm: 'scale(1.05)' },
                    boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.2)',
                  },
                  cursor: 'pointer',
                }}
                onClick={() => handleCardClick(apartment)}
              >
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    height: 200, // Fixed height for image section
                    backgroundColor: 'grey.300',
                    overflow: 'hidden',
                  }}
                >
                  <img
                    src={
                      apartment.photoURL?.length > 0
                        ? apartment.photoURL[0].photoURL
                        : '/placeholder.png'//'https://rentlord.online/placeholder.png'
                    }
                    alt={apartment.place_description || 'No Image Available'}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                  {!(apartment.photoURL?.length > 0) && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        minWidth: '100%',
                        height: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        backgroundColor: 'rgba(0, 0, 0, 0.2)', // Slight overlay for readability
                      }}
                    >
                    </Box>
                  )}
                  {apartment.rent_offer && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        backgroundColor: 'rgba(128, 0, 128, 0.3)',
                        color: 'white',
                        padding: 1,
                        borderBottomRightRadius: 1,
                      }}
                    >
                      <Typography variant="subtitle1">
                        {apartment.rent_offer.one_year_rent
                          ? `${apartment.rent_offer.currency} ${apartment.rent_offer.one_year_rent}`
                          : '$ On Call'}
                      </Typography>
                    </Box>
                  )}
                  {apartment.rent_preferences?.visa_status && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -30,
                        right: -45,
                        backgroundColor: 'rgba(3, 105, 64, 0.7)',
                        color: 'rgba(163, 246, 164, 0.7)',
                        padding: '6px 30px',
                        transform: 'rotate(45deg)',
                        transformOrigin: 'bottom left',
                        zIndex: 1,
                      }}
                    >
                      <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                        Visa friendly
                      </Typography>
                    </Box>
                  )}
                </Box>
                <CardContent
                  sx={{
                    height: 150, // Fixed height for content (350 - 200)
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    overflow: 'hidden', // Prevent content overflow
                  }}
                >
                  <Typography
                    variant="h7"
                    sx={{
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      maxWidth: '100%',
                      lineHeight: 1.9,
                    }}
                  >
                    {apartment.extracted_location || apartment.place_description}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '100%',
                      marginTop: 1,
                    }}
                  >
                    {apartment.square_footage ? `${apartment.square_footage} m²` : 'on call'}
                  </Typography>
                  <Box sx={{ mt: 0.8, overflow: 'hidden' }}>
                    <DetailItem
                      icon={MeetingRoomIcon}
                      label="Rooms"
                      value={
                        apartment.rooms != null && String(apartment.rooms).length > 10
                          ? `${String(apartment.rooms).substring(0, 10)}`
                          : apartment.rooms ?? 'on call'
                      }
                    />
                    <ParkingDisplay
                      parkingTypes={
                        apartment.parking_types != null && apartment.parking_types.length > 15
                          ? `${apartment.parking_types.substring(0, 15)}`
                          : apartment.parking_types ?? 'on call'
                      }
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid2>
          ))
        ) : (
          <Grid2 item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
              <Typography variant="h6" color="textSecondary">
                No apartments available at this time.
              </Typography>
            </Box>
          </Grid2>
        )}
      </Grid2>

      {selectedApartment && (
        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            {selectedApartment.place_description}
            <IconButton
              aria-label="close"
              onClick={handleCloseDialog}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <PhotoCarousel photos={selectedApartment.photoURL} />
            </Box>
            <Divider sx={{ my: 3 }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>  
              {/* Apartment Details */}
              {(selectedApartment.square_footage || selectedApartment.floor || 
                selectedApartment.bedrooms || selectedApartment.bathrooms || 
                selectedApartment.building_type || selectedApartment.construction_year) && (
                <Box>
                  <Typography variant="h6" color="secondary.main">Details</Typography>
                  {selectedApartment.square_footage && (
                    <DetailItem icon={SquareFootIcon} label="Size" value={`${selectedApartment.square_footage} m²`} />
                  )}
                  {selectedApartment.levels && (
                    <DetailItem icon={Balcony} label="Balcony" value={`${selectedApartment.levels} m²`} />
                  )}
                  {selectedApartment.floor && (
                    <DetailItem icon={FloorIcon} label="Floor" value={selectedApartment.floor} />
                  )}
                  {selectedApartment.bedrooms && (
                    <DetailItem icon={BedIcon} label="Bedrooms" value={selectedApartment.bedrooms} />
                  )}
                  {selectedApartment.bathrooms && (
                    <DetailItem icon={BathIcon} label="Bathrooms" value={selectedApartment.bathrooms} />
                  )}
                  {(selectedApartment.building_type || selectedApartment.construction_year) && (
                    <DetailItem icon={CalendarIcon} value={`${selectedApartment.building_type} from ${selectedApartment.construction_year}`} />
                  )}
                  {(selectedApartment?.rent_offer?.available_from) && (
                    <DetailItem icon={EventAvailable} label="Available from" value={selectedApartment.rent_offer.available_from} />
                  )}
                </Box>
              )}

              {/* Amenities */}
              {(selectedApartment.internet || selectedApartment.elevator || selectedApartment.security_camera || 
                selectedApartment.parking_types) && (
                <Box>
                  <Typography variant="h6" color="secondary.main">Amenities</Typography>
                  {selectedApartment.internet && <DetailItem icon={WifiIcon} value="Internet" />}
                  {selectedApartment.elevator && <DetailItem icon={ElevatorIcon} value="Elevator" />}
                  {selectedApartment.security_camera && <DetailItem icon={CameraOutdoorIcon} value="Security camera" />}
                  {selectedApartment.parking_types && <ParkingDisplay parkingTypes={selectedApartment.parking_types} />}
                </Box>
              )}

              {/* Contact */}
              {selectedApartment?.rent_offer?.contact_phone && (
                <Box>
                  <Typography variant="h6" color="secondary.main">Contact</Typography>
                  <DetailItem icon={PhoneAndroidIcon} label="Phone" value={selectedApartment.rent_offer.contact_phone} />
                </Box>
              )}

              {/* Monthly Costs */}
              {(selectedApartment?.rent_offer?.one_year_rent || selectedApartment?.rent_offer?.two_years_rent ||
                selectedApartment?.rent_offer?.three_years_rent || selectedApartment?.rent_offer?.residential_fee ||
                selectedApartment?.rent_offer?.bills_amount) && (
                <Box>
                  <Typography variant="h6" color="secondary.main">Monthly Costs</Typography>
                  {selectedApartment?.rent_offer?.one_year_rent && (
                    <DetailItem icon={PaymentIcon} label="Rent with 1 year agreement"
                      value={`${selectedApartment.rent_offer.currency} ${Number(selectedApartment.rent_offer.one_year_rent).toLocaleString()}`} />
                  )}
                  {selectedApartment?.rent_offer?.two_years_rent && (
                    <DetailItem icon={PaymentIcon} label="Rent with 2 years agreement"
                      value={`${selectedApartment.rent_offer.currency} ${Number(selectedApartment.rent_offer.two_years_rent).toLocaleString()}`} />
                  )}
                  {selectedApartment?.rent_offer?.three_years_rent && (
                    <DetailItem icon={PaymentIcon} label="Rent with 3 years agreement"
                      value={`${selectedApartment.rent_offer.currency} ${Number(selectedApartment.rent_offer.three_years_rent).toLocaleString()}`} />
                  )}
                  {selectedApartment?.rent_offer?.residential_fee && (
                    <DetailItem icon={PaymentIcon} label="Residential fee"
                      value={`${selectedApartment.rent_offer.currency} ${Number(selectedApartment.rent_offer.residential_fee).toLocaleString()}`} />
                  )}
                  {selectedApartment?.rent_offer?.bills_amount && (
                    <DetailItem icon={PaymentIcon} label="Bills"
                      value={`${selectedApartment.rent_offer.currency} ±${Number(selectedApartment.rent_offer.bills_amount).toLocaleString()}`} />
                  )}
                </Box>
              )}

              {/* Rent Preferences */}
              {(selectedApartment?.rent_preferences?.visa_status || selectedApartment?.rent_preferences?.student_status ||
                selectedApartment?.rent_preferences?.animals || selectedApartment?.rent_preferences?.kids ||
                selectedApartment?.rent_preferences?.couple || selectedApartment?.rent_preferences?.single) && (
                <Box>
                  <Typography variant="h6" color="secondary.main">Preferences</Typography>
                  {selectedApartment?.rent_preferences?.visa_status && <DetailItem icon={PublicIcon} value="Visa" />}
                  {selectedApartment?.rent_preferences?.student_status && <DetailItem icon={SchoolIcon} value="Student" />}
                  {selectedApartment?.rent_preferences?.animals && <DetailItem icon={PetsIcon} value="Pets" />}
                  {selectedApartment?.rent_preferences?.kids && <DetailItem icon={ChildFriendlyIcon} value="Children" />}
                  {selectedApartment?.rent_preferences?.couple && <DetailItem icon={CoupleIcon} value="Couple" />}
                  {selectedApartment?.rent_preferences?.single && <DetailItem icon={ManIcon} value="Single" />}
                </Box>
              )}

              {/* Equipment */}
              {(selectedApartment?.equipment?.sofa) && (
                <Box>
                  <Typography variant="h6" color="secondary.main">Equipment</Typography>
                  {selectedApartment?.equipment?.sofa && <DetailItem icon={PublicIcon} value={`sofa: ${selectedApartment.equipment.sofa}`} />}
                </Box>
              )}
            </Box>
          </DialogContent>
        </Dialog>
      )}
    </Container>
  );
};

export default ApartmentListing;